import { Icon } from "@iconify/react";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { parseAsInteger, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { useShuffleQuestions } from "$/modules/question/hooks/useShuffleQuestions";
import { useGetActiveQuestionnaire } from "$/modules/questionnaire/hooks/useGetActiveQuestionnaire";
import { SurveyQuizPlayerHeader } from "$/modules/quiz-player/survey/ui/survey-quiz-player-header";
import { SurveyQuizPlayerQuestion } from "$/modules/quiz-player/survey/ui/survey-quiz-player-question";
import { queryStates } from "$/modules/shared/lib/queryStates";
import { Button } from "$/modules/shared/ui/button";
import { Card, CardContent } from "$/modules/shared/ui/card";
import { Progress } from "$/modules/shared/ui/progress";

export const Route = createFileRoute(
	"/$locale/quiz-player/$projectId/questionnaires/$questionnaireId/",
)({
	component: QuizPage,
	loader: async () => {},
});

function QuizPage() {
	const activeQuestionnaire = useGetActiveQuestionnaire();
	const { locale, projectId } = useParams({
		from: "/$locale/quiz-player/$projectId/questionnaires/$questionnaireId/",
	});
	const navigate = useNavigate();

	const t = useTranslations("quiz");
	const shuffledQuestions = useShuffleQuestions(activeQuestionnaire?.questions);
	const [questionProgress] = useQueryState(
		queryStates.QUESTION,
		parseAsInteger.withDefault(0),
	);
	const [, setQuestionProgress] = useQueryState(
		queryStates.QUESTION,
		parseAsInteger.withDefault(0),
	);
	const [correctAnswers, setCorrectAnswers] = useState(0);
	const [result, setResult] = useState<{
		correct: number;
		total: number;
	} | null>(null);
	const progress = ((questionProgress + 1) / shuffledQuestions.length) * 100;
	const activeQuestion = shuffledQuestions[questionProgress];
	const scoredQuestions = shuffledQuestions.filter(
		(question) => question.questionType !== "Info",
	).length;

	if (!activeQuestionnaire) {
		return <div>{t("questionnaireNotFound")}</div>;
	}

	if (result) {
		const percentage = result.total
			? Math.round((result.correct / result.total) * 100)
			: 100;

		return (
			<div className="flex min-h-screen items-center justify-center bg-content2/70 px-4 py-8">
				<Card className="w-full max-w-xl rounded-[2rem] border border-default-200 bg-content1">
					<CardContent className="items-center gap-5 px-6 py-10 text-center sm:px-12">
						<div className="rounded-full bg-success/10 p-4 text-success">
							<Icon icon="lucide:circle-check-big" className="size-10" />
						</div>
						<div className="space-y-2">
							<p className="text-sm font-extrabold uppercase tracking-[0.14em] text-secondary">
								{t("surveyFinished")}
							</p>
							<h1 className="font-display text-3xl font-bold sm:text-4xl">
								{t("correctAnswers", result)}
							</h1>
							<p className="text-lg text-foreground-600">
								{t("percentCorrect", { percentage })}
							</p>
						</div>
						<Button
							onClick={async () => {
								await setQuestionProgress(0);
								await navigate({
									to: "/$locale/quiz-player/$projectId/questionnaires",
									params: { locale, projectId },
									replace: true,
								});
							}}
							variant="default"
							type="button"
						>
							{t("backToOverview")}
							{<Icon icon="lucide:arrow-right" />}
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-content2/70 px-4 py-5 sm:px-6 sm:py-8">
			<div className="mx-auto max-w-4xl">
				<SurveyQuizPlayerHeader
					title={activeQuestionnaire?.name || ""}
					questionProgress={questionProgress + 1}
					totalQuestions={shuffledQuestions.length}
				/>
				<Progress
					value={progress}
					className="mb-6"
					aria-label={t("progress")}
				/>
				<div>
					<SurveyQuizPlayerQuestion
						question={activeQuestion}
						maxQuestions={shuffledQuestions.length}
						onQuestionAnswered={(isCorrect) => {
							if (isCorrect) setCorrectAnswers((current) => current + 1);
						}}
						onQuizFinished={(isCorrect) => {
							setResult({
								correct: correctAnswers + (isCorrect ? 1 : 0),
								total: scoredQuestions,
							});
						}}
					/>
				</div>
			</div>
		</div>
	);
}
